/**
 * Icon registry.
 *
 * The legacy markup referenced icons as kebab-case `data-lucide` attributes and
 * called `lucide.createIcons()` to walk the DOM after every render. Components
 * here keep the same kebab-case names, but resolve them through this explicit
 * map.
 *
 * The map is explicit — rather than `import * as Icons from 'lucide-react'` —
 * because the namespace form pulls all ~1,500 lucide icons into the bundle and
 * defeats tree-shaking entirely. Naming only what is used keeps the icon cost
 * proportional to the icons actually rendered.
 *
 * To add an icon: add its kebab-case name and its PascalCase component here.
 */

import {
  Activity,
  AlarmClock,
  AlertCircle,
  AlertOctagon,
  AlertTriangle,
  Apple,
  Archive,
  ArrowDownCircle,
  ArrowLeft,
  ArrowRight,
  ArrowUpCircle,
  Baby,
  Ban,
  Bed,
  BookmarkPlus,
  Banknote,
  BarChart,
  BarChart2,
  Bell,
  BellOff,
  BellRing,
  Bone,
  Brain,
  Briefcase,
  Building,
  Building2,
  Calendar,
  CalendarCheck,
  CalendarCheck2,
  CalendarClock,
  CalendarDays,
  CalendarPlus,
  CalendarRange,
  CalendarOff,
  CalendarX,
  Camera,
  Check,
  CheckSquare,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  ClipboardList,
  ClipboardPlus,
  ClipboardX,
  Clock,
  Copy,
  CreditCard,
  Dice5,
  Download,
  Droplet,
  Ear,
  Edit3,
  Eye,
  SearchX,
  Sun,
  WandSparkles,
  ExternalLink,
  File,
  FileCheck2,
  FileQuestion,
  FileSearch,
  FileText,
  FileX,
  Film,
  Filter,
  FlaskConical,
  Folder,
  FolderOpen,
  Hammer,
  Layers,
  Hash,
  HeartPulse,
  History,
  Gauge,
  Crown,
  Cpu,
  CircleHelp,
  Square,
  Home,
  Hourglass,
  Image,
  ImagePlus,
  KeyRound,
  ImageOff,
  Inbox,
  Info,
  LayoutDashboard,
  LayoutGrid,
  Lightbulb,
  Link,
  List,
  ListChecks,
  ListX,
  Loader2,
  LogIn,
  LogOut,
  Mail,
  Map,
  MapPin,
  MapPinOff,
  Lock,
  Menu,
  PieChart,
  Pipette,
  Navigation,
  MessageSquareText,
  MonitorCheck,
  Microscope,
  Move,
  Network,
  Package,
  Paperclip,
  Palette,
  PackageX,
  Pause,
  Pencil,
  Percent,
  PersonStanding,
  Phone,
  Pill,
  Play,
  PlayCircle,
  Plus,
  PlusCircle,
  Printer,
  QrCode,
  Receipt,
  RefreshCw,
  Repeat,
  RotateCcw,
  RotateCw,
  Save,
  Scan,
  Scissors,
  Search,
  Send,
  Settings,
  Settings2,
  Share2,
  Shield,
  ShieldCheck,
  ShoppingCart,
  ShieldPlus,
  Smile,
  Smartphone,
  Sparkles,
  Stethoscope,
  StickyNote,
  Tag,
  Ticket,
  Timer,
  TicketCheck,
  TicketX,
  Trash2,
  TrendingDown,
  TrendingUp,
  Undo2,
  Unlink,
  Upload,
  UploadCloud,
  User,
  UserCheck,
  UserCog,
  UserPlus,
  UserX,
  Users,
  Utensils,
  Video,
  Wallet,
  Wind,
  Zap,
  X,
  XCircle,
  Ambulance,
  BedDouble,
  BriefcaseMedical,
  CalendarCog,
  CircleDollarSign,
  ClipboardCheck,
  Coins,
  DoorOpen,
  FileHeart,
  FilePlus2,
  HandCoins,
  Hospital,
  ListTodo,
  NotebookPen,
  PhoneCall,
  PhoneOff,
  Radiation,
  ScanLine,
  ScrollText,
  Siren,
  Syringe,
  TestTube,
  TestTubes,
  Thermometer,
  BadgeCheck,
  ShieldAlert,
  CheckCheck,
  ScanBarcode,
  TicketPlus,
  Handshake,
  Landmark,
  FolderPlus,
  IdCard,
  FileArchive,
  Music,
  Sheet,
  ZoomIn,
  ZoomOut,
  ChevronsLeft,
  ListOrdered,
  Megaphone,
  Monitor,
  SkipForward,
  Tv,
  SlidersHorizontal,
  FolderCheck,
  UserRound,
  CircleDashed,
  Contrast,
  Moon,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  Bot,
  LifeBuoy,
  Headset,
  ScanText,
  ScanEye,
  CalendarX2,
  Maximize2,
  Minimize2,
  MailOpen,
  Reply,
  Table2,
  CornerDownLeft,
  BookOpen,
  ToggleLeft,
  ToggleRight,
  Puzzle,
  ArrowRightLeft,
  Infinity as InfinityIcon,
  PackageCheck,
  PackagePlus,
  Braces,
  CircleCheck,
  Clock3,
  Code,
  EyeOff,
  FileLock,
  FileSpreadsheet,
  Fingerprint,
  Globe,
  KeySquare,
  Languages,
  Laptop,
  LockKeyhole,
  MailCheck,
  MonitorSmartphone,
  Scale,
  Server,
  ShieldX,
  TabletSmartphone,
  Terminal,
  Unlock,
  UserRoundCheck,
  Webhook,
  // المرحلة 1.3 — the doctor's card, the queue and the applications.
  Star,
  GraduationCap,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  FileBadge,
  FileX2,
  ThumbsUp,
  CalendarSearch,
} from 'lucide-react';

/** kebab-case name -> lucide component. */
export const ICONS = {
  // المرحلة 1.3
  'star': Star,
  'graduation-cap': GraduationCap,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'arrow-up-down': ArrowUpDown,
  'file-badge': FileBadge,
  'file-x-2': FileX2,
  'thumbs-up': ThumbsUp,
  'calendar-search': CalendarSearch,
  'activity': Activity,
  'alarm-clock': AlarmClock,
  'alert-circle': AlertCircle,
  'alert-octagon': AlertOctagon,
  'alert-triangle': AlertTriangle,
  'apple': Apple,
  'archive': Archive,
  'arrow-down-circle': ArrowDownCircle,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up-circle': ArrowUpCircle,
  'baby': Baby,
  'ban': Ban,
  'banknote': Banknote,
  'bar-chart': BarChart,
  'bar-chart-2': BarChart2,
  'bell': Bell,
  'bell-off': BellOff,
  'bell-ring': BellRing,
  'bone': Bone,
  'brain': Brain,
  'briefcase': Briefcase,
  'building': Building,
  'building-2': Building2,
  'calendar': Calendar,
  'calendar-check': CalendarCheck,
  'calendar-check-2': CalendarCheck2,
  'calendar-clock': CalendarClock,
  'calendar-days': CalendarDays,
  'calendar-plus': CalendarPlus,
  'calendar-range': CalendarRange,
  'calendar-off': CalendarOff,
  'calendar-x': CalendarX,
  'camera': Camera,
  'bed': Bed,
  'bookmark-plus': BookmarkPlus,
  'check': Check,
  'check-square': CheckSquare,
  'check-circle-2': CheckCircle2,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  'circle': Circle,
  'clipboard-list': ClipboardList,
  'clipboard-plus': ClipboardPlus,
  'clipboard-x': ClipboardX,
  'clock': Clock,
  'copy': Copy,
  'credit-card': CreditCard,
  'dice-5': Dice5,
  'download': Download,
  'droplet': Droplet,
  'ear': Ear,
  'edit-3': Edit3,
  'eye': Eye,
  'external-link': ExternalLink,
  'file': File,
  'file-check-2': FileCheck2,
  'file-question': FileQuestion,
  'file-search': FileSearch,
  'file-text': FileText,
  'file-x': FileX,
  'film': Film,
  'filter': Filter,
  'flask-conical': FlaskConical,
  'folder': Folder,
  'folder-open': FolderOpen,
  'hammer': Hammer,
  'layers': Layers,
  'hash': Hash,
  'heart-pulse': HeartPulse,
  'history': History,
  'gauge': Gauge,
  // Added for the AI console: 'crown' marks the premium tier and 'cpu' the
  // model that served a request.
  'crown': Crown,
  'cpu': Cpu,
  'help-circle': CircleHelp,
  'square': Square,
  'home': Home,
  'hourglass': Hourglass,
  'image': Image,
  'image-plus': ImagePlus,
  'key-round': KeyRound,
  'image-off': ImageOff,
  'inbox': Inbox,
  'info': Info,
  'layout-dashboard': LayoutDashboard,
  'layout-grid': LayoutGrid,
  'lightbulb': Lightbulb,
  'link': Link,
  'list': List,
  'list-checks': ListChecks,
  'list-x': ListX,
  'loader-2': Loader2,
  'log-in': LogIn,
  'log-out': LogOut,
  'mail': Mail,
  'map': Map,
  'map-pin': MapPin,
  'map-pin-off': MapPinOff,
  'lock': Lock,
  'menu': Menu,
  'navigation': Navigation,
  'message-square-text': MessageSquareText,
  'monitor-check': MonitorCheck,
  'microscope': Microscope,
  'move': Move,
  'network': Network,
  'package': Package,
  'paperclip': Paperclip,
  'palette': Palette,
  'package-x': PackageX,
  'pause': Pause,
  'pencil': Pencil,
  'percent': Percent,
  'person-standing': PersonStanding,
  'phone': Phone,
  'pie-chart': PieChart,
  'pipette': Pipette,
  'pill': Pill,
  'play': Play,
  'play-circle': PlayCircle,
  'plus': Plus,
  'plus-circle': PlusCircle,
  'printer': Printer,
  'qr-code': QrCode,
  'receipt': Receipt,
  'refresh-cw': RefreshCw,
  'repeat': Repeat,
  'rotate-ccw': RotateCcw,
  'rotate-cw': RotateCw,
  'save': Save,
  'scan': Scan,
  'scissors': Scissors,
  'search': Search,
  'search-x': SearchX,
  'sun': Sun,
  'wand-sparkles': WandSparkles,
  'send': Send,
  'settings': Settings,
  'settings-2': Settings2,
  'share-2': Share2,
  'shield': Shield,
  'shield-check': ShieldCheck,
  'shopping-cart': ShoppingCart,
  'shield-plus': ShieldPlus,
  'smile': Smile,
  'smartphone': Smartphone,
  'sparkles': Sparkles,
  'stethoscope': Stethoscope,
  'sticky-note': StickyNote,
  'tag': Tag,
  'ticket': Ticket,
  'timer': Timer,
  'ticket-check': TicketCheck,
  'ticket-x': TicketX,
  'trash-2': Trash2,
  'trending-down': TrendingDown,
  'trending-up': TrendingUp,
  'undo-2': Undo2,
  'unlink': Unlink,
  'upload': Upload,
  'upload-cloud': UploadCloud,
  'user': User,
  'user-check': UserCheck,
  'user-cog': UserCog,
  'user-plus': UserPlus,
  'user-x': UserX,
  'users': Users,
  'utensils': Utensils,
  'video': Video,
  'wallet': Wallet,
  'wind': Wind,
  'zap': Zap,
  'x': X,
  'x-circle': XCircle,

  // The 14-dashboard upgrade: departments, beds, money, and the nursing chart.
  'ambulance': Ambulance,
  'bed-double': BedDouble,
  'briefcase-medical': BriefcaseMedical,
  'calendar-cog': CalendarCog,
  'circle-dollar-sign': CircleDollarSign,
  'clipboard-check': ClipboardCheck,
  'coins': Coins,
  'door-open': DoorOpen,
  'file-heart': FileHeart,
  'file-plus-2': FilePlus2,
  'hand-coins': HandCoins,
  'hospital': Hospital,
  'list-todo': ListTodo,
  'notebook-pen': NotebookPen,
  'phone-call': PhoneCall,
  'phone-off': PhoneOff,
  'radiation': Radiation,
  'scan-line': ScanLine,
  'scroll-text': ScrollText,
  'siren': Siren,
  'syringe': Syringe,
  'test-tube': TestTube,
  'test-tubes': TestTubes,
  'thermometer': Thermometer,
  'badge-check': BadgeCheck,
  'shield-alert': ShieldAlert,
  'check-check': CheckCheck,
  'scan-barcode': ScanBarcode,
  'ticket-plus': TicketPlus,
  'handshake': Handshake,
  'landmark': Landmark,
  'folder-plus': FolderPlus,
  'id-card': IdCard,
  'file-archive': FileArchive,
  'music': Music,
  'sheet': Sheet,
  'zoom-in': ZoomIn,
  'zoom-out': ZoomOut,
  'chevrons-left': ChevronsLeft,
  'list-ordered': ListOrdered,
  'megaphone': Megaphone,
  'monitor': Monitor,
  'skip-forward': SkipForward,
  'tv': Tv,
  'sliders-horizontal': SlidersHorizontal,
  'folder-check': FolderCheck,
  'user-round': UserRound,
  'circle-dashed': CircleDashed,
  'contrast': Contrast,
  'moon': Moon,
  'message-circle': MessageCircle,
  'message-square': MessageSquare,
  'messages-square': MessagesSquare,
  'bot': Bot,
  'life-buoy': LifeBuoy,
  'headset': Headset,
  'scan-text': ScanText,
  'scan-eye': ScanEye,
  'calendar-x-2': CalendarX2,
  'maximize-2': Maximize2,
  'minimize-2': Minimize2,
  'mail-open': MailOpen,
  'reply': Reply,
  'table-2': Table2,
  'corner-down-left': CornerDownLeft,
  'book-open': BookOpen,
  'toggle-left': ToggleLeft,
  'toggle-right': ToggleRight,
  'puzzle': Puzzle,
  'arrow-right-left': ArrowRightLeft,
  'infinity': InfinityIcon,
  'package-check': PackageCheck,
  'package-plus': PackagePlus,
  // المرحلة 1.1: the languages, the sessions, the access log, exports and the API.
  'languages': Languages,
  'globe': Globe,
  'fingerprint': Fingerprint,
  'file-spreadsheet': FileSpreadsheet,
  'monitor-smartphone': MonitorSmartphone,
  'laptop': Laptop,
  'tablet-smartphone': TabletSmartphone,
  'code': Code,
  'webhook': Webhook,
  'lock-keyhole': LockKeyhole,
  'scale': Scale,
  'eye-off': EyeOff,
  'user-round-check': UserRoundCheck,
  'key-square': KeySquare,
  'server': Server,
  'clock-3': Clock3,
  'braces': Braces,
  'terminal': Terminal,
  'file-lock': FileLock,
  'mail-check': MailCheck,
  'circle-check': CircleCheck,
  'shield-x': ShieldX,
  'unlock': Unlock,
};

/** Shown when a name is not in the registry, so a typo is visible but harmless. */
export const FALLBACK_ICON = Circle;

export function resolveIcon(name) {
  return ICONS[name] || FALLBACK_ICON;
}
